/*! (c) 2026 Pro Sports Floors — psf freight-estimate widget v1.1
 *
 *  Product-page freight estimate for rubberflooringrolls.com.
 *  Pairs with snippets/psf-freight-estimate.liquid, which emits the #kfr-data
 *  island this file reads. See that snippet's header comment for why PSF runs
 *  a self-contained widget instead of Kodiak's site-wide zip-core stack.
 *
 *  Loaded with `defer`, so the DOM is parsed before this runs.
 */
(function () {
  'use strict';

  // Idempotence guard: the snippet renders once per product page, but a theme
  // editor preview or an app block can double-inject the script tag.
  if (window.__psfFreightLoaded) return;
  window.__psfFreightLoaded = true;

  // Never run inside checkout. Shopify's checkout does not use this layout, so
  // this is defence in depth against a future one-page-checkout extension.
  if (/^\/checkouts?\b/.test(location.pathname)) return;
  if (!/^\/products\//.test(location.pathname)) return;

  // ---------------------------------------------------------------- config --
  var ENDPOINT   = 'https://kodiak-freight.vercel.app/api/quote';
  var ZIP_KEY    = 'psf_freight_estimate_zip';   // {zip, at}
  var CACHE_KEY  = 'psf_freight_quote_cache';    // key -> {at, q}
  var CACHE_TTL  = 864e5;                        // 24h, matches Kodiak
  var CACHE_MAX  = 40;                           // entries before pruning to 30
  var GRAMS_PER_LB = 453.59237;
  var DEBOUNCE_MS  = 350;                        // lets OptionSelectors settle
  var POLL_MS      = 600;                        // see watchQuantity()

  // --------------------------------------------------------------- helpers --
  function money(n) {
    try {
      return '$' + Number(n).toLocaleString('en-US',
        { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } catch (e) {
      return '$' + Number(n).toFixed(2);
    }
  }

  function zips(raw) {
    return String(raw || '')
      .split(',')
      .map(function (z) { return z.trim(); })
      .filter(function (z) { return /^\d{5}$/.test(z); });
  }

  // localStorage is wrapped everywhere: it throws in private mode and in
  // cross-origin iframes (the theme editor preview is one).
  function lsGet(key) {
    try { return JSON.parse(localStorage.getItem(key) || 'null'); }
    catch (e) { return null; }
  }
  function lsSet(key, val) {
    try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) { /* no-op */ }
  }

  // ------------------------------------------------------------ zip memory --
  function savedZip() {
    var s = lsGet(ZIP_KEY);
    return s && /^\d{5}$/.test(s.zip || '') ? s.zip : null;
  }
  function saveZip(zip) { lsSet(ZIP_KEY, { zip: zip, at: Date.now() }); }

  // ----------------------------------------------------------- quote cache --
  // The key MUST include the surcharge. Without it, a surcharged rolled-rubber
  // SKU and an untagged product sharing an origin, a class and a weight bucket
  // collide on one key, and whichever was quoted first serves its price to the
  // other for 24h. This bug was found and fixed on Kodiak; it is not repeated
  // here. (theme/kodiak-freight-rate.PATCH.md, edit 3.)
  function bucket(lb) {
    var w = Math.max(1, Math.round(lb));
    return w <= 500 ? 25 * Math.ceil(w / 25) : 100 * Math.ceil(w / 100);
  }

  function cacheKey(zip, items) {
    return items.map(function (it) {
      return (it.origin_zips || []).join('+') + '~' +
             (it.freight_class || '') + '~' +
             (it.freight_surcharge_pct || 0) + '~' +
             bucket(it.weight_lbs);
    }).sort().join('|') + '@' + String(zip).slice(0, 3);
  }

  function cacheRead(key) {
    var all = lsGet(CACHE_KEY) || {};
    var hit = all[key];
    return (hit && Date.now() - hit.at < CACHE_TTL) ? hit.q : null;
  }

  function cacheWrite(key, quote) {
    var all = lsGet(CACHE_KEY) || {};
    var keys = Object.keys(all);
    if (keys.length > CACHE_MAX) {
      keys.sort(function (a, b) { return all[a].at - all[b].at; })
          .slice(0, keys.length - 30)
          .forEach(function (k) { delete all[k]; });
    }
    all[key] = { at: Date.now(), q: quote };
    lsSet(CACHE_KEY, all);
  }

  // ---------------------------------------------------------------- network --
  function fetchQuote(zip, items) {
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ destination_zip: zip, items: items })
    }).then(function (r) {
      if (!r.ok) throw new Error('quote ' + r.status);
      return r.json();
    });
  }

  function cachedQuote(zip, items) {
    var key = cacheKey(zip, items);
    var hit = cacheRead(key);
    if (hit) return Promise.resolve(hit);
    return fetchQuote(zip, items).then(function (q) {
      cacheWrite(key, q);
      return q;
    });
  }

  // ------------------------------------------------------------- page state --
  // Parsed once from the #kfr-data island. null means "nothing ratable here"
  // and the widget stays hidden — no origins, or every variant weighs 0.
  var data = (function parseIsland() {
    var el = document.getElementById('kfr-data');
    if (!el) return null;

    var raw;
    try { raw = JSON.parse(el.textContent); } catch (e) { return null; }

    var variantLb = {}, variantOrigins = {};
    var anyWeight = false, anyVariantOrigin = false;

    (raw.variants || []).forEach(function (v) {
      if (v.grams > 0) {
        variantLb[String(v.id)] = v.grams / GRAMS_PER_LB;
        anyWeight = true;
      }
      var vo = zips(v.origins);
      if (vo.length) {
        variantOrigins[String(v.id)] = vo;
        anyVariantOrigin = true;
      }
    });

    var origins = zips((raw.origins || []).join(','));
    if (!anyWeight) return null;                          // data hole, stay quiet
    if (!origins.length && !anyVariantOrigin) return null; // not ours to rate

    return {
      origins: origins,
      cls: parseInt(raw.freight_class, 10) || 0,
      classTagged: raw.freight_class_tagged === true,
      sur: parseInt(raw.freight_surcharge_pct, 10) || 0,
      variantLb: variantLb,
      variantOrigins: variantOrigins,
      title: raw.title || ''
    };
  }());

  if (!data) return;

  var shell = document.querySelector('[data-psf-freight]');
  if (!shell) return;

  var elForm     = shell.querySelector('[data-psf-form]');
  var elZip      = shell.querySelector('[data-psf-zip]');
  var elHead     = shell.querySelector('[data-psf-headline]');
  var elResult   = shell.querySelector('[data-psf-result]');
  var elComm     = shell.querySelector('[data-psf-commercial]');
  var elLift     = shell.querySelector('[data-psf-liftgate]');

  // ------------------------------------------------------- cart-line builder --
  function selectedVariantId() {
    var sel = document.querySelector('form[action*="/cart/add"] [name="id"]') ||
              document.getElementById('productSelect') ||
              document.querySelector('[name="id"]');
    return sel ? String(sel.value) : null;
  }

  function quantityEl() {
    return document.querySelector('form[action*="/cart/add"] [name="quantity"]') ||
           document.getElementById('quantity');
  }

  // Returns sq ft, or 0 meaning "the shopper has not said how much yet".
  //
  // Three product templates, two shapes:
  //   templates/product.liquid          -> a visible number input, defaults to 1
  //   product.rolled_rubber.liquid  \
  //   product.tile_calculator.liquid /  -> a HIDDEN input the on-page sq-ft
  //                                       calculator writes into, starting at 0
  //
  // 0 must NOT fall back to 1. On a calculator page that would quote one square
  // foot — a number near the $265 floor — next to an Add to Cart button for an
  // order the shopper has not sized yet. Absent field entirely still means 1,
  // because then one unit is genuinely all they can buy.
  function quantity() {
    var q = quantityEl();
    if (!q) return 1;
    var n = parseFloat(q.value);
    return n > 0 ? n : 0;
  }

  function buildItems() {
    var vid = selectedVariantId();
    if (!vid) return null;

    // Per-variant weight only. There is no product-level fallback: two colours
    // of the same thickness can differ, and a variant with no weight is a data
    // hole that must surface as "unavailable", never as someone else's weight.
    var unitLb = data.variantLb[vid];
    if (!(unitLb > 0)) return null;

    // Variant metafield origins override the product tag origins — a colour can
    // ship from a different plant, and the engine rates the shortest haul.
    var origins = data.variantOrigins[vid] || data.origins;
    if (!origins || !origins.length) return null;

    var qty = quantity();
    if (!(qty > 0)) return 'needs-qty';

    return [{
      // MAX_ORIGINS_PER_ITEM at /api/quote is 6; anything past that is dropped
      // server-side, so trim here to keep the cache key honest about what was
      // actually rated.
      origin_zips: origins.slice(0, 6),
      weight_lbs: unitLb * qty,
      freight_class: data.cls || undefined,
      freight_class_tagged: data.classTagged,
      freight_surcharge_pct: data.sur || undefined,
      title: data.title
    }];
  }

  // ------------------------------------------------------------- rendering --
  var seq = 0; // guards against an out-of-order response overwriting a newer one

  function showHead(html) { if (elHead) elHead.innerHTML = html; }

  function wireEdit() {
    var b = shell.querySelector('[data-psf-edit]');
    if (!b) return;
    b.addEventListener('click', function () {
      if (elForm) elForm.hidden = false;
      if (elZip) { elZip.focus(); elZip.select(); }
    });
  }

  function renderIdle() {
    if (elForm) elForm.hidden = false;
    if (elResult) elResult.hidden = true;
    showHead('Enter your ship-to ZIP for a live estimate');
  }

  function renderNeedsQty() {
    if (elForm) elForm.hidden = true;
    if (elResult) elResult.hidden = true;
    showHead('Enter your square footage above for a freight estimate');
  }

  // The snippet renders at the end of <body> (one layout-level render covers
  // every product template). Lift it to just below the Add to Cart form, where
  // the shopper is actually deciding. Runs once; if the form is missing the
  // shell simply stays where Liquid put it rather than disappearing.
  var moved = false;
  function reposition() {
    if (moved) return;
    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    form.insertAdjacentElement('afterend', shell);
    moved = true;
  }

  function renderLoading(zip) {
    showHead('Calculating for ' + zip + '&hellip;');
  }

  function renderFail(zip) {
    if (elResult) elResult.hidden = true;
    if (elForm) elForm.hidden = false;
    showHead('Freight for ' + (zip || 'this item') +
             ' is quoted at checkout. <br><small>Add to cart and enter your ' +
             'address, or call for a quote.</small>');
  }

  function renderQuote(zip, q) {
    if (!q || !q.shipments || !q.shipments.length) return renderFail(zip);

    if (elComm) elComm.textContent = money(q.commercial_total);
    if (elLift) elLift.textContent = money(q.residential_total);
    if (elResult) elResult.hidden = false;
    if (elForm) elForm.hidden = true;

    showHead('To <button type="button" class="psf-freight__edit" data-psf-edit>' +
             zip + ' &#9998;</button>');
    wireEdit();
  }

  // ----------------------------------------------------------------- driver --
  function refresh() {
    var items = buildItems();

    // No ratable line (unweighed or origin-less variant): hide rather than show
    // a stale price for the variant the customer just switched away from.
    if (!items) {
      shell.hidden = true;
      return;
    }

    reposition();
    shell.hidden = false;

    // Sized-but-not-yet-quantified is a real state, not a failure: show the
    // box so the shopper knows freight is calculated, without inventing a qty.
    if (items === 'needs-qty') return renderNeedsQty();

    var zip = (elZip && /^\d{5}$/.test((elZip.value || '').trim()))
      ? elZip.value.trim()
      : savedZip();

    if (!zip) return renderIdle();
    if (elZip && !elZip.value) elZip.value = zip;

    var mine = ++seq;
    renderLoading(zip);
    cachedQuote(zip, items).then(function (q) {
      if (mine === seq) renderQuote(zip, q);
    }).catch(function () {
      if (mine === seq) renderFail(zip);
    });
  }

  var timer;
  function refreshSoon() {
    clearTimeout(timer);
    timer = setTimeout(refresh, DEBOUNCE_MS);
  }

  // ------------------------------------------------------------- listeners --
  if (elForm) {
    elForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var zip = (elZip.value || '').trim();
      if (!/^\d{5}$/.test(zip)) {
        showHead('<span class="psf-freight__err">Enter a 5-digit ZIP code.</span>');
        return;
      }
      saveZip(zip);
      refresh();
    });
  }

  // Capture-phase, because Supply's Shopify.OptionSelectors rewrites the variant
  // select from its own change handler. The DEBOUNCE_MS delay is what lets that
  // finish before we read [name="id"] — reading synchronously here returns the
  // PREVIOUS variant.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.getAttribute) return;
    var name = t.getAttribute('name');
    var inCartForm = t.form && /\/cart\/add/.test(t.form.getAttribute('action') || '');
    var isSelector = t.className &&
                     String(t.className).indexOf('single-option-selector') !== -1;
    if (inCartForm || name === 'id' || name === 'quantity' || isSelector) refreshSoon();
  }, true);

  // `change` on a number input only fires on blur; sq-ft shoppers type and read.
  document.addEventListener('input', function (e) {
    var t = e.target;
    if (t && t.getAttribute && t.getAttribute('name') === 'quantity') refreshSoon();
  }, true);

  // enableHistoryState:true means ?variant= changes on selection; a Back press
  // swaps the variant without firing `change`.
  window.addEventListener('popstate', refreshSoon);

  // The sq-ft calculators write their result into a HIDDEN input by assigning
  // to .value. That fires no event and mutates no attribute, so neither a
  // listener nor a MutationObserver can see it — polling one property is the
  // only thing that actually works here, and it is cheap. (Kodiak watches a
  // visible text node with a MutationObserver instead; same problem, different
  // DOM.) Stops as soon as the page is hidden to stay off a background tab.
  (function watchQuantity() {
    var last = null;
    setInterval(function () {
      if (document.hidden) return;
      var q = quantityEl();
      if (!q) return;
      if (q.value !== last) {
        if (last !== null) refreshSoon();
        last = q.value;
      }
    }, POLL_MS);
  }());

  refresh();
}());
